import React, { useEffect } from "react";

/**
 * Small inline confirmation modal — title, body, primary + cancel buttons.
 * Escape closes. Click on backdrop closes. Focus traps to the primary button
 * on mount so keyboard users land on the action.
 */
export default function ConfirmModal({
  open,
  title,
  body,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  onConfirm,
  onClose,
  testId = "confirm-modal",
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const primaryColor = destructive ? "#AD393E" : "#4592A8";

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid={testId}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(26,26,26,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-paper"
        style={{
          maxWidth: "440px",
          width: "calc(100% - 32px)",
          padding: "28px 32px",
          border: "1px solid #E8E8E8",
          boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
        }}
      >
        <h2 style={{ fontFamily: "Oswald", fontSize: "20pt", color: "#1A1A1A", margin: 0, marginBottom: 8 }}>
          {title}
        </h2>
        <p className="text-sm text-gray-700" style={{ margin: 0, marginBottom: 24, lineHeight: 1.55 }}>
          {body}
        </p>
        <div className="flex items-center justify-end gap-3">
          <button onClick={onClose} className="btn-quiet" data-testid={`${testId}-cancel`}>
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            autoFocus
            data-testid={`${testId}-confirm`}
            style={{
              fontFamily: "Oswald",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              fontSize: "11px",
              background: primaryColor,
              color: "white",
              border: 0,
              padding: "10px 18px",
              cursor: "pointer",
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
